import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  TextField,
  Stack
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import api from '../../services/api';

const SnapshotManagementPanel = () => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, snapshot: null });
  const [createDialog, setCreateDialog] = useState({ open: false });
  const [detailDialog, setDetailDialog] = useState({ open: false, snapshot: null });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form state for creating new snapshot
  const [newSnapshotForm, setNewSnapshotForm] = useState({
    target_month: '',
    force: false,
    note: ''
  });

  const loadSnapshots = async () => {
    try {
      setLoading(true);
      const response = await api.get('/snapshots/');
      // Backend returns {results: [], summary: {}} structure
      const snapshotsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.results || response.data?.snapshots || []);
      setSnapshots(snapshotsData);
    } catch (err) {
      console.error('Error loading snapshots:', err);
      setError('Failed to load snapshots');
      // Ensure snapshots is set to empty array on error
      setSnapshots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSnapshots();
  }, []);

  const handleCreateSnapshot = async () => {
    try {
      setCreating(true);
      setError('');

      const payload = {
        target_month: newSnapshotForm.target_month,
        force: newSnapshotForm.force,
        note: newSnapshotForm.note
      };

      const response = await api.post('/snapshots/create_snapshot/', payload);
      setSuccess(`Successfully created snapshot: ${response.data.message}`);
      setCreateDialog({ open: false });
      setNewSnapshotForm({ target_month: '', force: false, note: '' });
      loadSnapshots(); // Reload the list
    } catch (err) {
      console.error('Error creating snapshot:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create snapshot';
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteSnapshot = async () => {
    try {
      setDeleting(true);
      setError('');

      await api.delete(`/snapshots/${deleteDialog.snapshot.id}/`);
      setSuccess('Snapshot deleted successfully');
      setDeleteDialog({ open: false, snapshot: null });
      loadSnapshots(); // Reload the list
    } catch (err) {
      console.error('Error deleting snapshot:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete snapshot';
      setError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const getSnapshotTypeChip = (snapshot) => {
    const note = snapshot.note || '';
    
    if (note.includes('Auto') || note.includes('Automatic')) {
      return <Chip label="Auto" color="success" size="small" />;
    } else if (note.includes('Import') || note.includes('Excel')) {
      return <Chip label="Import" color="primary" size="small" />;
    } else if (note.includes('Manual')) {
      return <Chip label="Manual" color="default" size="small" />;
    } else {
      return <Chip label="System" color="secondary" size="small" />;
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            System Monthly Snapshots
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage system snapshots for data backup and historical analysis
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSnapshots}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog({ open: true })}
          >
            Create Snapshot
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorageIcon color="primary" />
                <Box>
                  <Typography variant="h4" color="primary">
                    {Array.isArray(snapshots) ? snapshots.length : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Snapshots
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon color="success" />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {Array.isArray(snapshots) ? snapshots.filter(s => s.note?.includes('Auto')).length : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Auto Created
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DownloadIcon color="info" />
                <Box>
                  <Typography variant="h4" color="info.main">
                    {Array.isArray(snapshots) ? snapshots.filter(s => s.note?.includes('Import')).length : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Imported
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorageIcon color="warning" />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {Array.isArray(snapshots) ? snapshots.filter(s => s.is_locked).length : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Locked
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snapshots Table */}
      <Paper elevation={1}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Campaigns</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(snapshots) ? snapshots.map((snapshot) => (
                <TableRow key={snapshot.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {new Date(snapshot.month).toLocaleDateString('en-GB', { month: '2-digit', year: 'numeric' })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getSnapshotTypeChip(snapshot)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(snapshot.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {snapshot.created_by_id ? `User ${snapshot.created_by_id}` : 'System'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {snapshot.snapshot?.metadata?.total_campaigns || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={snapshot.is_locked ? 'Locked' : 'Active'} 
                      color={snapshot.is_locked ? 'warning' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ 
                      maxWidth: 200, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {snapshot.note || 'No note'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => setDetailDialog({ open: true, snapshot })}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Snapshot">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, snapshot })}
                          disabled={snapshot.is_locked}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No snapshots available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Snapshot Dialog */}
      <Dialog open={createDialog.open} onClose={() => setCreateDialog({ open: false })} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Create New Snapshot
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Create a new system snapshot for the specified month
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            {/* Target Month */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: 120 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  Target Month
                </Typography>
                <TextField
                  type="month"
                  value={newSnapshotForm.target_month}
                  onChange={(e) => setNewSnapshotForm(prev => ({ ...prev, target_month: e.target.value }))}
                  placeholder={getCurrentMonth()}
                  fullWidth
                  size="medium"
                  sx={{ 
                    '& .MuiInputBase-root': { 
                      height: 56 
                    } 
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Select the month for the snapshot (default: current month)
                </Typography>
              </Box>
            </Grid>

            {/* Force Create Option */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: 120 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  Options
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    height: 56, 
                    display: 'flex', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': { 
                      bgcolor: 'action.hover' 
                    }
                  }}
                  onClick={() => setNewSnapshotForm(prev => ({ ...prev, force: !prev.force }))}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <input
                      type="checkbox"
                      id="force-create"
                      checked={newSnapshotForm.force}
                      onChange={(e) => setNewSnapshotForm(prev => ({ ...prev, force: e.target.checked }))}
                      style={{ marginRight: 0 }}
                    />
                    <Typography variant="body1">
                      Force create (ignore date restrictions)
                    </Typography>
                  </Box>
                </Paper>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Override normal monthly snapshot creation rules
                </Typography>
              </Box>
            </Grid>

            {/* Note */}
            <Grid item xs={12}>
              <Box sx={{ minHeight: 140 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  Description Note
                </Typography>
                <TextField
                  value={newSnapshotForm.note}
                  onChange={(e) => setNewSnapshotForm(prev => ({ ...prev, note: e.target.value }))}
                  multiline
                  rows={3}
                  placeholder="Enter a description for this snapshot..."
                  fullWidth
                  size="medium"
                  sx={{ 
                    '& .MuiInputBase-root': { 
                      minHeight: 88 
                    } 
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Optional note to describe the purpose or context of this snapshot
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={() => setCreateDialog({ open: false })}
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateSnapshot} 
            variant="contained"
            disabled={creating}
            size="large"
            sx={{ minWidth: 140 }}
          >
            {creating ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Creating...
              </>
            ) : (
              'Create Snapshot'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, snapshot: null })}>
        <DialogTitle>Delete Snapshot</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the snapshot for {deleteDialog.snapshot?.month}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, snapshot: null })}>Cancel</Button>
          <Button 
            onClick={handleDeleteSnapshot} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, snapshot: null })} maxWidth="md" fullWidth>
        <DialogTitle>Snapshot Details</DialogTitle>
        <DialogContent>
          {detailDialog.snapshot && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Month</Typography>
                  <Typography variant="body1">{new Date(detailDialog.snapshot.month).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                  <Typography variant="body1">{formatDate(detailDialog.snapshot.created_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
                  <Typography variant="body1">
                    {detailDialog.snapshot.created_by_id ? `User ${detailDialog.snapshot.created_by_id}` : 'System'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Typography variant="body1">
                    <Chip 
                      label={detailDialog.snapshot.is_locked ? 'Locked' : 'Active'} 
                      color={detailDialog.snapshot.is_locked ? 'warning' : 'success'}
                      size="small"
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Total Campaigns</Typography>
                  <Typography variant="body1">
                    {detailDialog.snapshot.snapshot?.metadata?.total_campaigns || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Snapshot Type</Typography>
                  <Typography variant="body1">
                    {getSnapshotTypeChip(detailDialog.snapshot)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Note</Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {detailDialog.snapshot.note || 'No note provided'}
                  </Typography>
                </Grid>
                {detailDialog.snapshot.snapshot?.metadata && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Metadata</Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(detailDialog.snapshot.snapshot.metadata, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, snapshot: null })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SnapshotManagementPanel; 